import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IAppState } from 'infra/AppState'
import { broadcastEvent } from 'state/dashboard/dashboard.actions'
import {
  fetchGoogleDriveStatusAction,
  updateImageBackupDestinationAction
} from 'state/settings/settings.action'
import { IMAGE_BACKUP_DESTINATION } from 'services/ImageBackup/ImageBackupPreference'
import { openGoogleDriveAuthPopup } from 'services/Api/openGoogleDriveAuthPopup'
import GoogleDriveRequiredDialog from 'components/GoogleDriveRequiredDialog'

interface IUseRequireGoogleDriveResult {
  dialog: React.ReactElement
  ensure: (onReady: () => void) => void
}

export const useRequireGoogleDrive = (
  options: {
    // Body copy for the nudge; defaults to the bills wording
    message?: string
  } = {}
): IUseRequireGoogleDriveResult => {
  const dispatch = useDispatch()
  const gdrive = useSelector((state: IAppState) => state.Settings.gdrive)
  const [open, setOpen] = useState(false)
  const pendingActionRef = useRef<(() => void) | null>(null)
  const statusCheckedRef = useRef(false)

  // Bills can only be stored on web via a Drive URL, so backup must be both
  // connected AND set to Google Drive. Any other destination (NONE / never
  // chosen) means we prompt the user before they pick an image, instead of
  // letting them attach it and failing at submit time.
  const isBackupOn =
    gdrive.connected && gdrive.imageBackupDestination === IMAGE_BACKUP_DESTINATION.GOOGLE_DRIVE

  useEffect(() => {
    if (!statusCheckedRef.current) {
      statusCheckedRef.current = true
      dispatch(fetchGoogleDriveStatusAction())
    }
  }, [])

  useEffect(() => {
    if (isBackupOn && pendingActionRef.current) {
      // Drive got connected / switched on while this nudge was waiting (KMP GoogleDriveAdoptionViewModel)
      dispatch(broadcastEvent('gdrive_backup_connected', {}))
      const run = pendingActionRef.current
      pendingActionRef.current = null
      setOpen(false)
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackupOn])

  const ensure = useCallback(
    (onReady: () => void) => {
      if (isBackupOn) {
        onReady()
        return
      }
      pendingActionRef.current = onReady
      dispatch(broadcastEvent('gdrive_adoption_screen_viewed', {}))
      setOpen(true)
    },
    [isBackupOn, dispatch]
  )

  const handleClose = useCallback(() => {
    pendingActionRef.current = null
    setOpen(false)
  }, [])

  const handleConnectClick = useCallback(() => {
    dispatch(
      broadcastEvent('update_gdrive_image_backup', {
        // `gdrive_enabled` is what Android/iOS send; the destination is kept for existing web charts
        gdrive_enabled: true,
        image_backup_destination: IMAGE_BACKUP_DESTINATION.GOOGLE_DRIVE
      })
    )
    // Already authenticated with Drive — only the backup preference is off, so
    // just switch it on (no redundant auth popup) and continue the pending
    // action synchronously to keep the user gesture that opens the file picker.
    if (gdrive.connected) {
      dispatch(updateImageBackupDestinationAction(IMAGE_BACKUP_DESTINATION.GOOGLE_DRIVE))
      const run = pendingActionRef.current
      pendingActionRef.current = null
      setOpen(false)
      run?.()
      return
    }
    dispatch(broadcastEvent('gdrive_auth_required', {}))
    openGoogleDriveAuthPopup({
      onAuthComplete: () => {
        dispatch(fetchGoogleDriveStatusAction())
        dispatch(updateImageBackupDestinationAction(IMAGE_BACKUP_DESTINATION.GOOGLE_DRIVE))
      }
    })
  }, [dispatch, gdrive.connected])

  const dialog = (
    <GoogleDriveRequiredDialog
      open={open}
      onClose={handleClose}
      onConnectClick={handleConnectClick}
      message={options.message}
    />
  )

  return { dialog, ensure }
}
